import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { CacheService } from '@app/services/cache.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { UrlHelperService } from '@app/services/url-helper.service';
import { Environment } from '@environments/interface.environment';
import { ListItem } from '@microsoft/microsoft-graph-types-beta';
import { concat, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { SingleValueLegacyExtendedProperty } from '@microsoft/microsoft-graph-types';

@Injectable({
  providedIn: 'root'
})
export class ImageService {

  constructor(
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient,
    private urlHelperService: UrlHelperService,
    private featureSettingsStore: FeatureSettingsStoreService,
    private cache: CacheService,
    private httpHelper: HttpHelper,
    rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  private readonly cacheKeyPrefix = 'ImageService';
  private readonly contentAndQueryStringEndpoint = 'content?preferNoRedirect=true&clientType=modernWebPart&prefer=extendCacheMaxAge';
  public useCache = true;
  private renderer: Renderer2;

  private getThumbnailFromApiUrl$ = new Map<string, Observable<string>>();
  private getPublishingImageUrl$ = new Map<string, Observable<string>>();
  private getSharePointImageThumbnailFromOriginalUrlWithCustomDimensions$ = new Map<string, Observable<string>>();
  private getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions$ = new Map<string, Observable<string>>();

  resizeBase64Img(base64: string, origBlobSize: number): Observable<string> {

    const canvas: HTMLCanvasElement = this.renderer.createElement('canvas');
    const context: CanvasRenderingContext2D = canvas.getContext('2d');
    const img: HTMLImageElement = this.renderer.createElement('img');
    img.src = base64;

    return new Observable<string>((subscriber) => {
      img.onload = function () {
        let scale = 0.75; // Scale down to 75 %
        let quality = 0.92; // Default setting
        let error = false;
        if (origBlobSize > 6291456) {
          subscriber.error('Image was too large! Try again with a smaller size');
          error = true;
        } else if (origBlobSize > 5242880) {
          // If larger than 5 mb
          scale = 0.25;
          quality = 0.5;
        } else if (origBlobSize > 3145728) {
          // If larger than 3 mb
          scale = 0.4;
          quality = 0.6;
        } else if (origBlobSize > 2097152) {
          // If larger than 2 mb
          scale = 0.6;
          quality = 0.8;
        } else if (origBlobSize > 1048576) {
          // If larger than 1 mb
          scale = 0.6;
          quality = 0.8;
        } else {
          // run with default if under 1mb
        }

        if (!error) {
          // Set the canvas to the resulting "destination" size
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          // Paste our resized image using the params
          context.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', quality);

          subscriber.next(dataUrl);
        }

        subscriber.complete();
      };
    })
      .pipe(
        finalize(() => {
          img.remove();
          canvas.remove();
        })
      );
  }

  getThumbnailFromApiUrl(apiUrl: string): Observable<string> {
    if (this.getThumbnailFromApiUrl$.has(apiUrl)) {
      return this.getThumbnailFromApiUrl$.get(apiUrl);
    } else {
      this.getThumbnailFromApiUrl$.set(apiUrl,
        this.http.request('GET', apiUrl,
          {
            observe: 'response',
            responseType: 'blob',
          })
          .pipe(
            map(result => {
              return URL.createObjectURL(result.body);
            }),
            catchError(err => {
              if (err.status === 404) {
                return of(null);
              } else {
                return throwError(err);
              }
            }),
            shareReplay({ bufferSize: 1, refCount: true })));

      return this.getThumbnailFromApiUrl$.get(apiUrl);
    }
  }

  getPublishingImageUrl(originalUrl: string, fallbackImageUrl = Constants.TRANSPARENT_IMAGE): Observable<string> {
    if (this.getPublishingImageUrl$.has(originalUrl)) {
      return this.getPublishingImageUrl$.get(originalUrl);
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getPublishingImageUrl-${originalUrl}`;

      this.getPublishingImageUrl$.set(originalUrl, this.http.get<any>(originalUrl)
        .pipe(
          map(result => {
            const imageHtml = result['BoolField_x005f_InfoboxPicture'];
            if (imageHtml && imageHtml.length > 0) {
              const tmp = this.document.implementation.createHTMLDocument('virtual').createElement('div');
              tmp.innerHTML = imageHtml;
              const imageUrl = `${this.urlHelperService.getAuthorityUrlWithoutQueryStrings(originalUrl)}${tmp.querySelector('img').getAttribute('src')}`;
              tmp.remove();
              return imageUrl;
            } else {
              return fallbackImageUrl;
            }
          }),
          tap(result => {
            this.useCache && this.cache.set(cacheKey, result);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getPublishingImageUrl', result: Constants.TRANSPARENT_IMAGE, cacheKey: cacheKey, showErrorSnackbar: false })),
          shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<string>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getPublishingImageUrl$.get(originalUrl));
        }
      }

      return this.getPublishingImageUrl$.get(originalUrl);
    }
  }

  // Obtains a large thumbnail of an image in SPO from the getpreviewhandler endpoint. Thumbnail is generated by SharePoint
  getSharePointImageThumbnailFromPreviewHandlerUrl(originalUrl: string, size: string = this.featureSettingsStore.state.defaultImageThumbnailSize): Observable<string> {
    const hostName = this.urlHelperService.getHostName(originalUrl);
    const params = this.urlHelperService.getParamsFromUrl(originalUrl);
    if (params.has('path') && !params.has('guidFile')) {
      return this.getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions(params.get('path'));
    } else {
      const apiUrl = `https://${hostName}/_api/v2.1/sites/${hostName},${params.get('guidSite')},${params.get('guidWeb')}/items/${params.get('guidFile')}/driveItem/thumbnails/0/${size}/${this.contentAndQueryStringEndpoint}`;
      return this.getThumbnailFromApiUrl(apiUrl)
        .pipe(
          catchError(this.handleImageRedirectError())
        );
    }
  }

  // Obtains a thumbnail of an image in SPO. Thumbnail is generated by SharePoint
  getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions(originalUrl: string, size: string = this.featureSettingsStore.state.defaultImageThumbnailSize): Observable<string> {
    if (originalUrl.length > 0) {
      originalUrl = originalUrl[0] === '/' ? this.environment.tenantHostUrl + originalUrl : originalUrl;
    }
    if (this.getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions$.has(originalUrl)) {
      return this.getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions$.get(originalUrl);
    } else {
      const httpParams = new HttpParams()
        .append('url', `'${originalUrl}'`)
        .append('select', `id,parentReference`);
      this.getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions$.set(originalUrl,
        this.http.get<ListItem>(`${this.environment.coreWorkplaceSiteUrl}/_api/v2.0/sharePoint/getBaseItemByUrl`, { params: httpParams })
          .pipe(
            mergeMap(listItem => {
              if (listItem?.id && listItem?.parentReference?.id && listItem?.parentReference?.siteId && listItem?.parentReference['listId']) {
                const authorityUrl = this.urlHelperService.getAuthorityUrlWithoutQueryStrings(originalUrl);
                const hostName = this.urlHelperService.getHostName(originalUrl);
                let url = `${authorityUrl}/_api/v2.1/sites/${hostName},${listItem.parentReference.siteId}/lists/${listItem?.parentReference['listId']}/items/${listItem.id}`;
                url = `${url}/driveItem/thumbnails/0/${size}/${this.contentAndQueryStringEndpoint}`;
                return this.getThumbnailFromApiUrl(url);
              } else {
                return of(originalUrl);
              }
            }),
            catchError(this.handleImageRedirectError()),
            catchError(this.httpHelper.handleError({ operation: 'getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions', result: originalUrl, showErrorSnackbar: false })),
            shareReplay({ bufferSize: 1, refCount: true })));

      return this.getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions$.get(originalUrl);
    }
  }

  // Obtains a thumbnail of an image in SPO. Thumbnail is generated by SharePoint
  getSharePointImageThumbnailFromOriginalUrlWithCustomDimensions(originalUrl: string, width: number, height: number): Observable<string> {
    if (originalUrl.length > 0) {
      originalUrl = originalUrl[0] === '/' ? this.environment.tenantHostUrl + originalUrl : originalUrl;
    }

    if (this.getSharePointImageThumbnailFromOriginalUrlWithCustomDimensions$.has(originalUrl)) {
      return this.getSharePointImageThumbnailFromOriginalUrlWithCustomDimensions$.get(originalUrl);
    } else {
      const httpParams = new HttpParams()
        .append('url', `'${originalUrl}'`)
        .append('select', `id,parentReference`);
      this.getSharePointImageThumbnailFromOriginalUrlWithCustomDimensions$.set(originalUrl,
        this.http.get<ListItem>(`${this.environment.coreWorkplaceSiteUrl}/_api/v2.0/sharePoint/getBaseItemByUrl`, { params: httpParams })
          .pipe(
            mergeMap(listItem => {
              if (listItem?.id && listItem?.parentReference?.id && listItem?.parentReference?.siteId && listItem?.parentReference['listId']) {
                const authorityUrl = this.urlHelperService.getAuthorityUrlWithoutQueryStrings(originalUrl);
                const hostName = this.urlHelperService.getHostName(originalUrl);
                let url = `${authorityUrl}/_api/v2.1/sites/${hostName},${listItem.parentReference.siteId}/lists/${listItem?.parentReference['listId']}/items/${listItem.id}`;
                url = `${url}/driveItem/thumbnails/0/c${width}x${height}/${this.contentAndQueryStringEndpoint}`;
                return this.getThumbnailFromApiUrl(url);
              } else {
                return of(originalUrl);
              }
            }),
            catchError(this.handleImageRedirectError()),
            catchError(this.httpHelper.handleError({ operation: 'getSharePointImageThumbnailFromOriginalUrlWithCustomDimensions', result: Constants.TRANSPARENT_IMAGE, showErrorSnackbar: false })),
            shareReplay({ bufferSize: 1, refCount: true })));

      return this.getSharePointImageThumbnailFromOriginalUrlWithCustomDimensions$.get(originalUrl);
    }
  }

  private handleImageRedirectError(): (err: any, caught: Observable<string>) => Observable<string> {
    return (err: HttpErrorResponse) => {
      if (err?.status === 0 && err?.url) {
        // In some cases the browser (e.g. MS Edge legacy) wont follow the automatic redirect that may occur when sharepoint cant deliver a cached image from the normal url.
        // In those cases we try to fetch the actual url to the image service which is something like this: https://northeurope1-mediap.svc.ms/transform/thumbnail?provider=spo&inputFormat=jpg&cs=xxx&docid=https://tenant.sharepoint.com/_api/v2.0/drives/{driveid}/items/{itemdid}?tempauth=XXXX&version=Publishe&width=000&height=000&cb=0000
        // and then return it directly. It is a bit slower but it will atleast return the image and not our fallback transparent image.
        return this.http.get<SingleValueLegacyExtendedProperty>(err.url.replace(`/${this.contentAndQueryStringEndpoint}`, '/url'))
          .pipe(
            map((result => {
              return result?.value;
            }))
          );
      } else {
        return throwError(err);
      }
    };
  }
}
